import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { OPS_SERVICE_LABELS, type OpsService } from '@cortex/ops-core';
import type { OpsAssignmentEvent } from '../../../lib/api/ops.js';

/** ISO 時刻を "YYYY-MM-DD HH:mm"（UTC・locale 非依存）に整形する。 @graph-connects none */
function formatStamp(iso: string): string {
  return iso.slice(0, 16).replace('T', ' ');
}

/** AssignmentHistory の props。 */
interface AssignmentHistoryProps {
  /** 記録済みロスター変更（新しい順）。空なら「履歴なし」を表示する。 */
  events: OpsAssignmentEvent[];
}

/**
 * 記録済みロスター変更の実績タイムライン。projection（現在ロスターからの再計算）は一切せず、
 * 実際に保存された変更だけをサービス別・新しい順で表示する。記録が無ければ何も作らず空表示にする
 * （バックフィル不可なので履歴は導入時点から溜まっていく）。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 担当割当の「実際に起きたロスター変更」をサービス別タイムラインで表示する。記録が無ければ空表示（作り話をしない）
 * @graph-connects none
 */
export function AssignmentHistory({ events }: AssignmentHistoryProps): ReactNode {
  const { t } = useTranslation();
  if (events.length === 0) {
    return (
      <Card padding="md">
        <p className="text-sm text-[var(--color-text-muted)]">
          {t('ops.assignmentHistory.emptyState')}
        </p>
      </Card>
    );
  }

  // events は新しい順。サービスごとに初出順でグルーピングする。
  const services: OpsService[] = [];
  const byService = new Map<OpsService, OpsAssignmentEvent[]>();
  for (const event of events) {
    const list = byService.get(event.service);
    if (list === undefined) {
      services.push(event.service);
      byService.set(event.service, [event]);
    } else {
      list.push(event);
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-[var(--color-text-muted)]">{t('ops.assignmentHistory.intro')}</p>
      {services.map((service) => (
        <Card key={service} padding="md">
          <div className="text-sm font-semibold text-[var(--color-text-primary)]">
            {OPS_SERVICE_LABELS[service]}
          </div>
          <ul className="mt-2 space-y-2">
            {(byService.get(service) ?? []).map((event, index) => (
              <li
                key={`${event.effectiveFrom}-${String(index)}`}
                className="border-l-2 border-[var(--color-border)] pl-3 text-xs"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-[var(--color-text-secondary)]">
                    {formatStamp(event.effectiveFrom)}
                  </span>
                  <span className="text-[var(--color-text-muted)]">
                    {t('ops.assignmentHistory.by', { who: event.updatedBy })}
                  </span>
                  {event.covers.map((cover) => (
                    <Badge key={`${cover.date}-${cover.role}`} variant="warning" size="sm">
                      {t('ops.assignmentHistory.coverItem', {
                        date: cover.date,
                        role: cover.role,
                        who: cover.who,
                      })}
                    </Badge>
                  ))}
                </div>
                <div className="mt-1 text-[var(--color-text-muted)]">
                  {t('ops.assignmentHistory.opetanLine', { cadence: event.opetanCadence })}{' '}
                  {event.opetanQueue.length === 0 ? '—' : event.opetanQueue.join(' → ')}
                </div>
                <div className="text-[var(--color-text-muted)]">
                  {t('ops.assignmentHistory.reviewerLine', { cadence: event.reviewerCadence })}{' '}
                  {event.reviewerQueue.length === 0 ? '—' : event.reviewerQueue.join(' → ')}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}
